import React, { useEffect, useMemo, useState } from "react";
import { WorkspaceService } from "../services/workspaceService";
import {
  LandingPageConfig,
  QRCodeForm,
  QRDesignTemplate,
  QRFolder,
  QRSchedule,
  QRUTMTemplate,
  SmartRedirectRule,
  UTMSettings,
} from "../types";

interface Props {
  value: QRCodeForm;
  onChange: (updates: Partial<QRCodeForm>) => void;
  disabled?: boolean;
}

const inputClass = "app-input mt-2";
const createRule = (): SmartRedirectRule => ({
  id: `rule_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
  enabled: true,
  type: "device",
  value: "mobile",
  target: "",
  priority: 10,
});

const AdvancedQRCodeEditor: React.FC<Props> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [tagDraft, setTagDraft] = useState("");
  const [password, setPassword] = useState("");
  const [folders, setFolders] = useState<QRFolder[]>([]);
  const [templates, setTemplates] = useState<QRDesignTemplate[]>([]);
  const [templateName, setTemplateName] = useState("");
  const [utmTemplates, setUtmTemplates] = useState<QRUTMTemplate[]>([]);
  const [utmTemplateName, setUtmTemplateName] = useState("");
  const [tokenLabel, setTokenLabel] = useState("Gastzugang");
  const [generatedAccessLink, setGeneratedAccessLink] = useState("");
  const utm = useMemo<UTMSettings>(
    () => value.utm || { enabled: false },
    [value.utm],
  );
  const schedule = value.schedule || {
    enabled: false,
    timezone: "Europe/Berlin",
  };
  const protection = value.protection || { enabled: false };
  const landing = value.landingPage || {
    enabled: false,
    template: "links",
    leadFields: ["name", "email"],
  };
  const rules = value.smartRules || [];

  useEffect(() => {
    void Promise.all([
      WorkspaceService.getFolders(),
      WorkspaceService.getTemplates(),
      WorkspaceService.getUTMTemplates(),
    ])
      .then(([nextFolders, nextTemplates, nextUTMTemplates]) => {
        setFolders(nextFolders);
        setTemplates(nextTemplates);
        setUtmTemplates(nextUTMTemplates);
      })
      .catch((error) => console.error("Builder resources failed", error));
  }, []);

  const previewTarget = useMemo(() => {
    if (!utm.enabled || !value.target) return value.target;
    try {
      const url = new URL(value.target);
      const fields: Array<[string, string | undefined]> = [
        ["utm_source", utm.source],
        ["utm_medium", utm.medium],
        ["utm_campaign", utm.campaign],
        ["utm_term", utm.term],
        ["utm_content", utm.content],
      ];
      fields.forEach(([key, fieldValue]) => {
        if (fieldValue) url.searchParams.set(key, fieldValue);
      });
      (utm.customParameters || []).forEach((parameter) => {
        if (parameter.key && parameter.value)
          url.searchParams.set(parameter.key, parameter.value);
      });
      return url.toString();
    } catch {
      return value.target;
    }
  }, [utm, value.target]);

  const updateUTM = (updates: Partial<UTMSettings>) =>
    onChange({ utm: { ...utm, ...updates } });
  const updateSchedule = (updates: Partial<QRSchedule>) =>
    onChange({ schedule: { ...schedule, ...updates } });
  const updateLanding = (updates: Partial<LandingPageConfig>) =>
    onChange({ landingPage: { ...landing, ...updates } });
  const applyLandingTemplate = (template: LandingPageConfig["template"]) => {
    const defaults = landingTemplateDefaults[template || "links"];
    updateLanding({
      template,
      headline: landing.headline || defaults.headline,
      subheadline: landing.subheadline || defaults.subheadline,
      body: landing.body || defaults.body,
      buttonLabel: landing.buttonLabel || defaults.buttonLabel,
    });
  };

  const addTag = () => {
    const tag = tagDraft.trim();
    if (!tag || (value.tags || []).includes(tag)) return;
    onChange({ tags: [...(value.tags || []), tag] });
    setTagDraft("");
  };

  const updateRule = (id: string, updates: Partial<SmartRedirectRule>) =>
    onChange({
      smartRules: rules.map((rule) =>
        rule.id === id ? { ...rule, ...updates } : rule,
      ),
    });

  const updatePassword = async (nextPassword: string) => {
    setPassword(nextPassword);
    if (!nextPassword) return;
    const saltBytes = crypto.getRandomValues(new Uint8Array(16));
    const salt = Array.from(saltBytes)
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
    const key = await crypto.subtle.importKey(
      "raw",
      new TextEncoder().encode(nextPassword),
      "PBKDF2",
      false,
      ["deriveBits"],
    );
    const iterations = 120000;
    const derived = await crypto.subtle.deriveBits(
      { name: "PBKDF2", hash: "SHA-256", salt: saltBytes, iterations },
      key,
      256,
    );
    const hash = Array.from(new Uint8Array(derived))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
    onChange({
      protection: {
        ...protection,
        enabled: true,
        passwordHash: hash,
        passwordSalt: salt,
        passwordIterations: iterations,
      },
    });
  };

  const generateAccessLink = async () => {
    const rawToken = `${crypto.randomUUID()}${crypto.randomUUID()}`.replace(
      /-/g,
      "",
    );
    const digest = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(rawToken),
    );
    const tokenHash = Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
    const accessTokens = [
      ...(protection.accessTokens || []),
      {
        id: crypto.randomUUID(),
        label: tokenLabel.trim() || "Zugang",
        tokenHash,
        used: false,
      },
    ];
    onChange({ protection: { ...protection, enabled: true, accessTokens } });
    setGeneratedAccessLink(
      `${window.location.origin}/qr/${encodeURIComponent(value.id)}?access=${rawToken}`,
    );
  };

  return (
    <section className="app-panel overflow-hidden">
      <div className="border-b border-slate-200 px-5 py-5 sm:px-6">
        <div className="flex items-start gap-3">
          <span className="builder-step">3</span>
          <div>
            <h2 className="text-base font-semibold text-slate-950">
              Automation & Kampagne
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Organisation, Regeln, Schutz und eigene mobile Inhalte.
            </p>
          </div>
        </div>
      </div>
      <div className="divide-y divide-slate-200">
        <EditorSection
          title="Organisation"
          description="Tags, Notizen, Fallback und interner Änderungsgrund."
          defaultOpen
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="app-label" htmlFor="folderId">
                Ordner
              </label>
              <select
                id="folderId"
                value={value.folderId || ""}
                onChange={(event) => onChange({ folderId: event.target.value })}
                className={inputClass}
              >
                <option value="">Kein Ordner</option>
                {folders.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="app-label" htmlFor="fallbackTarget">
                Sicheres Fallback-Ziel
              </label>
              <input
                id="fallbackTarget"
                type="url"
                value={value.fallbackTarget || ""}
                onChange={(event) =>
                  onChange({ fallbackTarget: event.target.value })
                }
                placeholder={value.target || "https://…"}
                className={inputClass}
                disabled={disabled}
              />
              <p className="mt-2 text-xs text-slate-500">
                Wird verwendet, wenn Zeitplan oder Regel kein gültiges Ziel
                liefert.
              </p>
            </div>
            <div>
              <label className="app-label" htmlFor="changeComment">
                Kommentar zur Änderung
              </label>
              <input
                id="changeComment"
                value={value.changeComment || ""}
                onChange={(event) =>
                  onChange({ changeComment: event.target.value })
                }
                placeholder="z. B. neue Sommerkarte"
                className={inputClass}
                disabled={disabled}
              />
            </div>
          </div>
          <div className="mt-5">
            <label className="app-label" htmlFor="notes">
              Interne Notizen
            </label>
            <textarea
              id="notes"
              value={value.notes || ""}
              onChange={(event) => onChange({ notes: event.target.value })}
              placeholder="Druckort, Ansprechpartner oder Kampagnenhinweise"
              className={`${inputClass} min-h-[88px]`}
              disabled={disabled}
            />
          </div>
          <div className="mt-5">
            <label className="app-label" htmlFor="tagDraft">
              Tags
            </label>
            <div className="mt-2 flex gap-2">
              <input
                id="tagDraft"
                value={tagDraft}
                onChange={(event) => setTagDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addTag();
                  }
                }}
                placeholder="Tag hinzufügen"
                className="app-input"
                disabled={disabled}
              />
              <button
                type="button"
                onClick={addTag}
                className="app-secondary-button"
              >
                Hinzufügen
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {(value.tags || []).map((tag) => (
                <button
                  type="button"
                  key={tag}
                  onClick={() =>
                    onChange({
                      tags: (value.tags || []).filter((item) => item !== tag),
                    })
                  }
                  className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-red-50 hover:text-red-600"
                >
                  {tag} ×
                </button>
              ))}
            </div>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-[1fr_auto]">
            <select
              defaultValue=""
              onChange={(event) => {
                const template = templates.find(
                  (item) => item.id === event.target.value,
                );
                if (template) onChange({ styleOptions: template.styleOptions });
              }}
              className="app-input"
            >
              <option value="">Designvorlage anwenden …</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
            <div className="flex gap-2">
              <input
                value={templateName}
                onChange={(event) => setTemplateName(event.target.value)}
                className="app-input"
                placeholder="Vorlagenname"
              />
              <button
                type="button"
                onClick={async () => {
                  if (!templateName.trim() || !value.styleOptions) return;
                  await WorkspaceService.saveTemplate(
                    templateName,
                    value.styleOptions,
                  );
                  setTemplates(await WorkspaceService.getTemplates());
                  setTemplateName("");
                }}
                className="app-secondary-button"
              >
                Design speichern
              </button>
            </div>
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="app-label">Eigene Domain</label>
              <input
                value={value.customDomain || ""}
                onChange={(event) =>
                  onChange({
                    customDomain: event.target.value
                      .trim()
                      .toLowerCase()
                      .replace(/^https?:\/\//, "")
                      .replace(/\/$/, ""),
                  })
                }
                className={inputClass}
                placeholder="qr.meinefirma.de"
              />
            </div>
            <div>
              <label className="app-label">Eigener Pfad</label>
              <input
                value={value.customSlug || ""}
                onChange={(event) =>
                  onChange({
                    customSlug: event.target.value.replace(
                      /[^a-zA-Z0-9_-]/g,
                      "",
                    ),
                  })
                }
                className={inputClass}
                placeholder="sommerkarte"
              />
            </div>
          </div>
        </EditorSection>

        <EditorSection
          title="UTM-Kampagne"
          description="Marketingparameter automatisch an jedes Ziel anhängen."
        >
          <Toggle
            checked={Boolean(utm.enabled)}
            onChange={(checked) => updateUTM({ enabled: checked })}
            label="UTM-Parameter aktivieren"
          />
          {utm.enabled && (
            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              <select
                defaultValue=""
                onChange={(event) => {
                  const preset = utmTemplates.find(
                    (item) => item.id === event.target.value,
                  );
                  if (preset) onChange({ utm: preset.utm });
                }}
                className="app-input"
              >
                <option value="">UTM-Vorlage anwenden …</option>
                {utmTemplates.map((preset) => (
                  <option key={preset.id} value={preset.id}>
                    {preset.name}
                  </option>
                ))}
              </select>
              <input
                value={utmTemplateName}
                onChange={(event) => setUtmTemplateName(event.target.value)}
                className="app-input"
                placeholder="Vorlagenname"
              />
              <button
                type="button"
                onClick={async () => {
                  if (!utmTemplateName.trim()) return;
                  await WorkspaceService.saveUTMTemplate(utmTemplateName, utm);
                  setUtmTemplates(await WorkspaceService.getUTMTemplates());
                  setUtmTemplateName("");
                }}
                className="app-secondary-button"
              >
                UTM speichern
              </button>
            </div>
          )}
          {utm.enabled && (
            <>
              <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <UTMField
                  label="Quelle"
                  value={utm.source}
                  placeholder="flyer"
                  onChange={(source) => updateUTM({ source })}
                />
                <UTMField
                  label="Medium"
                  value={utm.medium}
                  placeholder="qr"
                  onChange={(medium) => updateUTM({ medium })}
                />
                <UTMField
                  label="Kampagne"
                  value={utm.campaign}
                  placeholder="sommer-2026"
                  onChange={(campaign) => updateUTM({ campaign })}
                />
                <UTMField
                  label="Begriff"
                  value={utm.term}
                  placeholder="optional"
                  onChange={(term) => updateUTM({ term })}
                />
                <UTMField
                  label="Inhalt"
                  value={utm.content}
                  placeholder="poster-a"
                  onChange={(content) => updateUTM({ content })}
                />
              </div>
              <div className="mt-5 space-y-2">
                {(utm.customParameters || []).map((parameter, index) => (
                  <div
                    key={index}
                    className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]"
                  >
                    <input
                      value={parameter.key}
                      onChange={(event) =>
                        updateUTM({
                          customParameters: (utm.customParameters || []).map(
                            (item, itemIndex) =>
                              itemIndex === index
                                ? { ...item, key: event.target.value }
                                : item,
                          ),
                        })
                      }
                      className="app-input"
                      placeholder="Parameter"
                    />
                    <input
                      value={parameter.value}
                      onChange={(event) =>
                        updateUTM({
                          customParameters: (utm.customParameters || []).map(
                            (item, itemIndex) =>
                              itemIndex === index
                                ? { ...item, value: event.target.value }
                                : item,
                          ),
                        })
                      }
                      className="app-input"
                      placeholder="Wert"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        updateUTM({
                          customParameters: (utm.customParameters || []).filter(
                            (_, itemIndex) => itemIndex !== index,
                          ),
                        })
                      }
                      className="app-secondary-button"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    updateUTM({
                      customParameters: [
                        ...(utm.customParameters || []),
                        { key: "", value: "" },
                      ],
                    })
                  }
                  className="text-xs font-semibold text-blue-600"
                >
                  + Eigener Parameter
                </button>
              </div>
              <div className="mt-4 rounded-xl bg-slate-50 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  Vorschau
                </p>
                <p className="mt-1 break-all font-mono text-xs text-slate-600">
                  {previewTarget}
                </p>
              </div>
            </>
          )}
        </EditorSection>

        <EditorSection
          title="Zeitplanung"
          description="Vor, während und nach der Kampagne automatisch das richtige Ziel öffnen."
        >
          <Toggle
            checked={Boolean(schedule.enabled)}
            onChange={(checked) => updateSchedule({ enabled: checked })}
            label="Zeitplan aktivieren"
          />
          {schedule.enabled && (
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="app-label">Aktiv ab</label>
                <input
                  type="datetime-local"
                  value={toZonedDateTime(schedule.startsAt, schedule.timezone)}
                  onChange={(event) =>
                    updateSchedule({
                      startsAt: zonedDateTimeToISO(
                        event.target.value,
                        schedule.timezone,
                      ),
                    })
                  }
                  className={inputClass}
                />
              </div>
              <div>
                <label className="app-label">Aktiv bis</label>
                <input
                  type="datetime-local"
                  value={toZonedDateTime(schedule.endsAt, schedule.timezone)}
                  onChange={(event) =>
                    updateSchedule({
                      endsAt: zonedDateTimeToISO(
                        event.target.value,
                        schedule.timezone,
                      ),
                    })
                  }
                  className={inputClass}
                />
              </div>
              <div>
                <label className="app-label">Ziel vorher</label>
                <input
                  type="url"
                  value={schedule.beforeTarget || ""}
                  onChange={(event) =>
                    updateSchedule({ beforeTarget: event.target.value })
                  }
                  className={inputClass}
                  placeholder="Fallback, wenn leer"
                />
              </div>
              <div>
                <label className="app-label">Ziel nachher</label>
                <input
                  type="url"
                  value={schedule.afterTarget || ""}
                  onChange={(event) =>
                    updateSchedule({ afterTarget: event.target.value })
                  }
                  className={inputClass}
                  placeholder="Fallback, wenn leer"
                />
              </div>
              <div>
                <label className="app-label">Zeitzone</label>
                <select
                  value={schedule.timezone || "Europe/Berlin"}
                  onChange={(event) =>
                    updateSchedule({ timezone: event.target.value })
                  }
                  className={inputClass}
                >
                  <option>Europe/Berlin</option>
                  <option>Europe/London</option>
                  <option>America/New_York</option>
                  <option>Asia/Tokyo</option>
                  <option>UTC</option>
                </select>
              </div>
            </div>
          )}
        </EditorSection>

        <EditorSection
          title="Smart Redirects"
          description="Priorisierte Ziele nach Gerät, Sprache, Wochentag, Uhrzeit oder Land."
        >
          <div className="space-y-4">
            {rules.map((rule, index) => (
              <div
                key={rule.id}
                className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">Regel {index + 1}</p>
                  <button
                    type="button"
                    onClick={() =>
                      onChange({
                        smartRules: rules.filter((item) => item.id !== rule.id),
                      })
                    }
                    className="text-xs font-semibold text-red-600"
                  >
                    Entfernen
                  </button>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-[150px_1fr_1.4fr_90px]">
                  <select
                    value={rule.type}
                    onChange={(event) =>
                      updateRule(rule.id, {
                        type: event.target.value as SmartRedirectRule["type"],
                      })
                    }
                    className="app-input"
                  >
                    <option value="device">Gerät/OS</option>
                    <option value="language">Sprache</option>
                    <option value="weekday">Wochentag</option>
                    <option value="time">Uhrzeit</option>
                    <option value="country">Land</option>
                  </select>
                  <input
                    value={rule.value}
                    onChange={(event) =>
                      updateRule(rule.id, { value: event.target.value })
                    }
                    className="app-input"
                    placeholder={rulePlaceholder(rule.type)}
                  />
                  <input
                    type="url"
                    value={rule.target}
                    onChange={(event) =>
                      updateRule(rule.id, { target: event.target.value })
                    }
                    className="app-input"
                    placeholder="https://ziel.de"
                  />
                  <input
                    type="number"
                    min="1"
                    value={rule.priority}
                    onChange={(event) =>
                      updateRule(rule.id, {
                        priority: Number(event.target.value),
                      })
                    }
                    className="app-input"
                    title="Priorität"
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => onChange({ smartRules: [...rules, createRule()] })}
              className="app-secondary-button"
            >
              + Regel hinzufügen
            </button>
          </div>
        </EditorSection>

        <EditorSection
          title="Zugriffsschutz"
          description="Passwort, Ablauf, Scanlimit, Länder und Einmalzugriff."
        >
          <Toggle
            checked={Boolean(protection.enabled)}
            onChange={(checked) =>
              onChange({ protection: { ...protection, enabled: checked } })
            }
            label="Zugriffsschutz aktivieren"
          />
          {protection.enabled && (
            <>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="app-label">Neues Passwort</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(event) =>
                      void updatePassword(event.target.value)
                    }
                    className={inputClass}
                    placeholder={
                      protection.passwordHash
                        ? "Passwort bleibt unverändert"
                        : "Optional"
                    }
                  />
                </div>
                <div>
                  <label className="app-label">Passworthinweis</label>
                  <input
                    value={protection.passwordHint || ""}
                    onChange={(event) =>
                      onChange({
                        protection: {
                          ...protection,
                          passwordHint: event.target.value,
                        },
                      })
                    }
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="app-label">Zugriff bis</label>
                  <input
                    type="datetime-local"
                    value={toLocalDateTime(protection.expiresAt)}
                    onChange={(event) =>
                      onChange({
                        protection: {
                          ...protection,
                          expiresAt: toISOString(event.target.value),
                        },
                      })
                    }
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="app-label">Maximale Scans</label>
                  <input
                    type="number"
                    min="0"
                    value={protection.maxScans || 0}
                    onChange={(event) =>
                      onChange({
                        protection: {
                          ...protection,
                          maxScans: Number(event.target.value),
                        },
                      })
                    }
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="app-label">Erlaubte Länder</label>
                  <input
                    value={(protection.allowedCountries || []).join(", ")}
                    onChange={(event) =>
                      onChange({
                        protection: {
                          ...protection,
                          allowedCountries: event.target.value
                            .split(",")
                            .map((item) => item.trim().toUpperCase())
                            .filter(Boolean),
                        },
                      })
                    }
                    className={inputClass}
                    placeholder="DE, AT, CH"
                  />
                </div>
                <div className="pt-7">
                  <Toggle
                    checked={Boolean(protection.oneTime)}
                    onChange={(oneTime) =>
                      onChange({ protection: { ...protection, oneTime } })
                    }
                    label="Nur ein erfolgreicher Scan"
                  />
                </div>
              </div>
              <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-semibold">
                  Individueller Einmal-Link
                </p>
                <div className="mt-3 flex gap-2">
                  <input
                    value={tokenLabel}
                    onChange={(event) => setTokenLabel(event.target.value)}
                    className="app-input"
                    placeholder="Bezeichnung"
                  />
                  <button
                    type="button"
                    onClick={() => void generateAccessLink()}
                    className="app-secondary-button"
                  >
                    Link erzeugen
                  </button>
                </div>
                {generatedAccessLink && (
                  <div className="mt-3 rounded-xl bg-white p-3">
                    <p className="break-all font-mono text-xs text-slate-600">
                      {generatedAccessLink}
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        void navigator.clipboard.writeText(generatedAccessLink)
                      }
                      className="mt-2 text-xs font-semibold text-blue-600"
                    >
                      Link kopieren
                    </button>
                  </div>
                )}
                <div className="mt-3 space-y-2">
                  {(protection.accessTokens || []).map((token) => (
                    <div
                      key={token.id}
                      className="flex items-center justify-between text-xs"
                    >
                      <span>
                        {token.label}{" "}
                        {token.used ? "· verwendet" : "· verfügbar"}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          onChange({
                            protection: {
                              ...protection,
                              accessTokens: (
                                protection.accessTokens || []
                              ).filter((item) => item.id !== token.id),
                            },
                          })
                        }
                        className="font-semibold text-red-600"
                      >
                        Entfernen
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </EditorSection>

        <EditorSection
          title="Mobile Landingpage & Leads"
          description="Eigene Seite statt sofortiger Weiterleitung anzeigen."
        >
          <Toggle
            checked={Boolean(landing.enabled)}
            onChange={(checked) => updateLanding({ enabled: checked })}
            label="Landingpage aktivieren"
          />
          {landing.enabled && (
            <div className="mt-5 space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="app-label">Vorlage</label>
                  <select
                    value={landing.template || "links"}
                    onChange={(event) =>
                      applyLandingTemplate(
                        event.target.value as LandingPageConfig["template"],
                      )
                    }
                    className={inputClass}
                  >
                    <option value="links">Link-in-Bio</option>
                    <option value="business-card">Digitale Visitenkarte</option>
                    <option value="menu">Speisekarte</option>
                    <option value="product">Produkt</option>
                    <option value="real-estate">Immobilie</option>
                    <option value="event">Event</option>
                    <option value="review">Bewertung</option>
                    <option value="contact">Kontakt</option>
                    <option value="download">Downloadseite</option>
                    <option value="social">Social-Media-Seite</option>
                  </select>
                </div>
                <div>
                  <label className="app-label">Markenfarbe</label>
                  <input
                    type="color"
                    value={landing.themeColor || "#2563eb"}
                    onChange={(event) =>
                      updateLanding({ themeColor: event.target.value })
                    }
                    className="mt-2 h-11 w-full rounded-xl border border-slate-200 bg-white p-1"
                  />
                </div>
                <div>
                  <label className="app-label">Überschrift</label>
                  <input
                    value={landing.headline || ""}
                    onChange={(event) =>
                      updateLanding({ headline: event.target.value })
                    }
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="app-label">Unterzeile</label>
                  <input
                    value={landing.subheadline || ""}
                    onChange={(event) =>
                      updateLanding({ subheadline: event.target.value })
                    }
                    className={inputClass}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="app-label">Inhalt</label>
                  <textarea
                    value={landing.body || ""}
                    onChange={(event) =>
                      updateLanding({ body: event.target.value })
                    }
                    className={`${inputClass} min-h-[96px]`}
                  />
                </div>
                <div>
                  <label className="app-label">Bild-URL</label>
                  <input
                    type="url"
                    value={landing.imageUrl || ""}
                    onChange={(event) =>
                      updateLanding({ imageUrl: event.target.value })
                    }
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="app-label">Button-Ziel</label>
                  <input
                    type="url"
                    value={landing.buttonUrl || ""}
                    onChange={(event) =>
                      updateLanding({ buttonUrl: event.target.value })
                    }
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="app-label">Button-Text</label>
                  <input
                    value={landing.buttonLabel || ""}
                    onChange={(event) =>
                      updateLanding({ buttonLabel: event.target.value })
                    }
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <p className="app-label">Zusätzliche Links</p>
                {(landing.links || []).map((link, index) => (
                  <div
                    key={link.id}
                    className="grid gap-2 sm:grid-cols-[1fr_1.5fr_auto]"
                  >
                    <input
                      value={link.label}
                      onChange={(event) =>
                        updateLanding({
                          links: (landing.links || []).map((item) =>
                            item.id === link.id
                              ? { ...item, label: event.target.value }
                              : item,
                          ),
                        })
                      }
                      className="app-input"
                      placeholder="Beschriftung"
                    />
                    <input
                      type="url"
                      value={link.url}
                      onChange={(event) =>
                        updateLanding({
                          links: (landing.links || []).map((item) =>
                            item.id === link.id
                              ? { ...item, url: event.target.value }
                              : item,
                          ),
                        })
                      }
                      className="app-input"
                      placeholder="https://…"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        updateLanding({
                          links: (landing.links || []).filter(
                            (item) => item.id !== link.id,
                          ),
                        })
                      }
                      className="app-secondary-button"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    updateLanding({
                      links: [
                        ...(landing.links || []),
                        { id: crypto.randomUUID(), label: "", url: "" },
                      ],
                    })
                  }
                  className="text-xs font-semibold text-blue-600"
                >
                  + Link hinzufügen
                </button>
              </div>
              <Toggle
                checked={Boolean(landing.collectLeads)}
                onChange={(collectLeads) =>
                  updateLanding({
                    collectLeads,
                    leadFields: landing.leadFields?.length
                      ? landing.leadFields
                      : ["name", "email"],
                  })
                }
                label="Kontaktanfragen sammeln"
              />
              {landing.collectLeads && (
                <div className="flex flex-wrap gap-3">
                  {(["name", "email", "phone", "message"] as const).map(
                    (field) => (
                      <label
                        key={field}
                        className="inline-flex items-center gap-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={(landing.leadFields || []).includes(field)}
                          onChange={(event) =>
                            updateLanding({
                              leadFields: event.target.checked
                                ? [...(landing.leadFields || []), field]
                                : (landing.leadFields || []).filter(
                                    (item) => item !== field,
                                  ),
                            })
                          }
                        />
                        {
                          (
                            {
                              name: "Name",
                              email: "E-Mail",
                              phone: "Telefon",
                              message: "Nachricht",
                            } as const
                          )[field]
                        }
                      </label>
                    ),
                  )}
                </div>
              )}
            </div>
          )}
        </EditorSection>
      </div>
    </section>
  );
};

const EditorSection: React.FC<{
  title: string;
  description: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, description, defaultOpen, children }) => (
  <details open={defaultOpen} className="group p-5 sm:p-6">
    <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-950">{title}</h3>
        <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>
      </div>
      <span className="text-xl text-slate-400 transition group-open:rotate-45">
        +
      </span>
    </summary>
    <div className="pt-5">{children}</div>
  </details>
);
const Toggle: React.FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}> = ({ checked, onChange, label }) => (
  <label className="inline-flex cursor-pointer items-center gap-3 text-sm font-semibold text-slate-700">
    <input
      type="checkbox"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      className="h-4 w-4 rounded border-slate-300 text-blue-600"
    />
    {label}
  </label>
);
const UTMField: React.FC<{
  label: string;
  value?: string;
  placeholder: string;
  onChange: (value: string) => void;
}> = ({ label, value, placeholder, onChange }) => (
  <div>
    <label className="app-label">{label}</label>
    <input
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className={inputClass}
    />
  </div>
);
const toISOString = (value: string) =>
  value ? new Date(value).toISOString() : "";
const toLocalDateTime = (value?: string) =>
  value
    ? new Date(
        new Date(value).getTime() - new Date(value).getTimezoneOffset() * 60000,
      )
        .toISOString()
        .slice(0, 16)
    : "";
const toZonedDateTime = (value?: string, timeZone = "Europe/Berlin") => {
  if (!value) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(value));
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value || "";
  return `${read("year")}-${read("month")}-${read("day")}T${read("hour")}:${read("minute")}`;
};
const zonedDateTimeToISO = (value: string, timeZone = "Europe/Berlin") => {
  if (!value) return "";
  const [datePart, timePart] = value.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute] = timePart.split(":").map(Number);
  let instant = Date.UTC(year, month - 1, day, hour, minute);
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const rendered = toZonedDateTime(new Date(instant).toISOString(), timeZone);
    const renderedAsUTC = Date.parse(`${rendered}:00Z`);
    const desiredAsUTC = Date.UTC(year, month - 1, day, hour, minute);
    instant += desiredAsUTC - renderedAsUTC;
  }
  return new Date(instant).toISOString();
};
const landingTemplateDefaults: Record<
  NonNullable<LandingPageConfig["template"]>,
  Pick<LandingPageConfig, "headline" | "subheadline" | "body" | "buttonLabel">
> = {
  links: {
    headline: "Alle wichtigen Links",
    subheadline: "Finde uns überall",
    body: "Wähle den passenden Kanal.",
    buttonLabel: "Mehr erfahren",
  },
  "business-card": {
    headline: "Schön, dich kennenzulernen",
    subheadline: "Digitale Visitenkarte",
    body: "Kontaktdaten, Leistungen und direkte Kontaktmöglichkeiten.",
    buttonLabel: "Kontakt aufnehmen",
  },
  menu: {
    headline: "Unsere Speisekarte",
    subheadline: "Frisch. Einfach. Lecker.",
    body: "Entdecke unser aktuelles Angebot.",
    buttonLabel: "Speisekarte öffnen",
  },
  product: {
    headline: "Produkt entdecken",
    subheadline: "Alle Details auf einen Blick",
    body: "Vorteile, Eigenschaften und weitere Informationen.",
    buttonLabel: "Jetzt ansehen",
  },
  "real-estate": {
    headline: "Diese Immobilie entdecken",
    subheadline: "Exposé und Besichtigung",
    body: "Alle Eckdaten und der direkte Kontakt zur Anfrage.",
    buttonLabel: "Exposé öffnen",
  },
  event: {
    headline: "Du bist eingeladen",
    subheadline: "Alle Infos zum Event",
    body: "Termin, Ort und Anmeldung auf einen Blick.",
    buttonLabel: "Jetzt anmelden",
  },
  review: {
    headline: "Wie war dein Erlebnis?",
    subheadline: "Deine Meinung zählt",
    body: "Hilf uns mit einer kurzen Bewertung.",
    buttonLabel: "Jetzt bewerten",
  },
  contact: {
    headline: "Kontakt aufnehmen",
    subheadline: "Wir sind für dich da",
    body: "Schreib uns oder ruf direkt an.",
    buttonLabel: "Nachricht senden",
  },
  download: {
    headline: "Dein Download",
    subheadline: "Datei direkt herunterladen",
    body: "Klicke auf den Button, um den Download zu starten.",
    buttonLabel: "Jetzt herunterladen",
  },
  social: {
    headline: "Folge uns",
    subheadline: "Social Media an einem Ort",
    body: "News, Einblicke und aktuelle Inhalte.",
    buttonLabel: "Profil öffnen",
  },
};
const rulePlaceholder = (type: SmartRedirectRule["type"]) =>
  ({
    device: "mobile, iOS, Android",
    language: "de, en",
    weekday: "sat, sun",
    time: "08:00-12:00",
    country: "DE, AT",
  })[type];

export default AdvancedQRCodeEditor;
